import type { MusicVideoSongMapSection } from 'fym-shared/src/music24/musicVideoApi';

/** The colour of a section in the song map bar (design directed-video `.sg.s-*`). */
export type SectionTone = 'intro' | 'verse' | 'chorus' | 'bridge' | 'outro';

export interface SectionBarItem {
  index: number;
  tone: SectionTone;
  /** The short name in the bar: "V1", "C2"; empty for the intro and the outro. */
  short: string;
  /** The full name of the song map ("verse 1"), for the title of the part. */
  label: string;
  /** The length of the section: the flex share of the bar. */
  lengthSec: number;
}

const TONES: Readonly<Record<string, SectionTone>> = {
  intro: 'intro',
  verse: 'verse',
  chorus: 'chorus',
  instrumental: 'bridge',
  outro: 'outro',
  section: 'verse',
};

const LETTERS: Readonly<Record<string, string>> = { verse: 'V', chorus: 'C', section: 'S' };

/**
 * The parts of the song map bar (design directed-video b1 "Sections", DvSide, j3): one part per
 * section, a colour per kind, a short name ("V1", "C1", "B"), the length as the share. Pure.
 */
export function sectionBarItems(sections: readonly MusicVideoSongMapSection[]): SectionBarItem[] {
  const counts = new Map<string, number>();
  return sections.map((section) => {
    const tone = sectionToneOf(section.kind);
    const letter = LETTERS[section.kind];
    let short = '';
    if (letter !== undefined) {
      const count = (counts.get(section.kind) ?? 0) + 1;
      counts.set(section.kind, count);
      short = `${letter}${count}`;
    } else if (tone === 'bridge') {
      short = 'B';
    }
    return {
      index: section.index,
      tone,
      short,
      label: section.label,
      lengthSec: Math.max(0.1, section.endSec - section.startSec),
    };
  });
}

/** The colour of a section kind (design `.sg.s-*`). An unknown kind is a verse. Pure. */
export function sectionToneOf(kind: string): SectionTone {
  return TONES[kind] ?? 'verse';
}

/** The name of a section on screen: "Verse 1" for "verse 1". Pure. */
export function sectionDisplayName(label: string): string {
  const trimmed = label.trim();
  return trimmed === '' ? trimmed : `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}`;
}

/** The section that holds the time `sec`, else the last section that starts before it, else null. Pure. */
export function sectionAt<T extends Pick<MusicVideoSongMapSection, 'startSec' | 'endSec'>>(
  sections: readonly T[],
  sec: number
): T | null {
  let found: T | null = null;
  for (const section of sections) {
    if (section.startSec <= sec + 0.001) {
      found = section;
    }
    if (sec >= section.startSec - 0.001 && sec < section.endSec) {
      return section;
    }
  }
  return found;
}
